import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { earlyUserOffer } from "../lib/ai";
import { ApiError, api, type PublicConfig } from "../lib/api";
import { useSession } from "../lib/session";
import { Alert, Button, Card, Field, TextInput, buttonClass, cx } from "../components/ui";

export const GOOGLE_ERRORS: Record<string, string> = {
  google_not_configured: "Google sign-in isn't available yet. Use your email instead.",
  google_cancelled: "Google sign-in was cancelled.",
  google_state: "That Google sign-in expired or was opened in a different browser. Please try again.",
  google_exchange: "Google didn't respond. Please try again.",
  google_token: "Google sign-in failed. Please try again.",
  google_unverified: "Your Google account's email isn't verified. Use the email link instead.",
  google_mismatch: "This email is linked to a different Google account. Use the email link instead.",
};

/** Catches the common typos before sending, without blocking unusual but valid addresses. */
export function emailTypoHint(email: string): string | null {
  const domain = email.trim().toLowerCase().split("@")[1];
  if (!domain) return null;
  const fixes: Record<string, string> = {
    "gmial.com": "gmail.com",
    "gamil.com": "gmail.com",
    "gmai.com": "gmail.com",
    "gmail.co": "gmail.com",
    "gmail.con": "gmail.com",
    "hotmial.com": "hotmail.com",
    "yahooo.com": "yahoo.com",
    "outlok.com": "outlook.com",
  };
  return fixes[domain] ? `Did you mean ${email.trim().split("@")[0]}@${fixes[domain]}?` : null;
}

declare global {
  interface Window {
    turnstile?: { render: (el: HTMLElement, opts: { sitekey: string; callback: (t: string) => void; "expired-callback": () => void }) => string; reset: (id?: string) => void };
  }
}

function Turnstile({ siteKey, onToken }: { siteKey: string; onToken: (t: string | null) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const render = () => ref.current && window.turnstile?.render(ref.current, { sitekey: siteKey, callback: onToken, "expired-callback": () => onToken(null) });
    if (window.turnstile) return void render();
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.onload = render;
    document.head.appendChild(script);
  }, [siteKey, onToken]);
  return <div ref={ref} />;
}

export function SignIn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { me } = useSession();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(params.get("error") ? (GOOGLE_ERRORS[params.get("error")!] ?? "Sign-in failed. Please try again.") : null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const next = params.get("next") ?? "/";

  useEffect(() => {
    if (me) navigate(next.startsWith("/") && !next.startsWith("//") ? next : "/", { replace: true });
  }, [me, navigate, next]);
  useEffect(() => {
    api<PublicConfig>("/api/config").then(setConfig, () => setConfig({ googleEnabled: false, turnstileSiteKey: null, environment: "unknown" }));
  }, []);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function send(e?: FormEvent) {
    e?.preventDefault();
    if (sending || cooldown > 0) return;
    const cleaned = email.trim();
    if (!cleaned) return setError("Enter your email address.");
    if (!cleaned.includes("@")) return setError("That doesn't look like an email address. It needs an @.");
    if (config?.turnstileSiteKey && !turnstileToken) return setError("Please complete the human check first.");
    setSending(true);
    setError(null);
    try {
      const res = await api<{ ok: true; devLink?: string }>("/api/auth/magic-link", {
        method: "POST",
        json: { email: cleaned, next, turnstileToken: turnstileToken ?? undefined },
      });
      setSentTo(cleaned.toLowerCase());
      setDevLink(res.devLink ?? null);
      setCooldown(30);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send the email. Please try again.");
      if (window.turnstile) window.turnstile.reset();
      setTurnstileToken(null);
    } finally {
      setSending(false);
    }
  }

  const typo = emailTypoHint(email);
  const earlyOffer = earlyUserOffer(config?.earlyUsers);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[radial-gradient(ellipse_at_top,var(--ck-accent-soft),transparent_60%)] px-4 py-10">
      <Card className="w-full max-w-sm space-y-6 shadow-lg sm:p-8">
        <div className="space-y-2 text-center">
          <img src="/icons/icon.svg" alt="" className="mx-auto h-12 w-12" />
          <h1 className="text-xl font-semibold tracking-tight">Sign in to ClauseKeep</h1>
          <p className="text-sm text-muted">Never miss a renewal or notice deadline. Free, no card needed.</p>
        </div>
        {earlyOffer && !sentTo && <p className="rounded-lg bg-accent-soft px-3 py-2 text-center text-sm font-medium text-accent-soft-fg">{earlyOffer}</p>}
        {error && <Alert tone="bad">{error}</Alert>}
        {sentTo ? (
          <div className="space-y-4">
            <Alert tone="good">
              We sent a sign-in link to <strong>{sentTo}</strong>. Open it on any device. It expires in 15 minutes. Can't find it? Check spam.
            </Alert>
            {devLink && (
              <p className="break-all text-xs text-faint">
                Local development: <a className="font-medium text-accent-fg underline underline-offset-2" href={devLink}>open the link</a>
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => void send()} disabled={cooldown > 0 || sending}>
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend"}
              </Button>
              <Button variant="ghost" onClick={() => { setSentTo(null); setDevLink(null); }}>
                Use a different email
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={send} noValidate className="space-y-4">
            <Field label="Email" hint={typo ?? undefined}>
              {(id, d) => (
                <TextInput id={id} type="email" name="email" autoComplete="email" inputMode="email" required aria-describedby={d} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
              )}
            </Field>
            {config?.turnstileSiteKey && <Turnstile siteKey={config.turnstileSiteKey} onToken={setTurnstileToken} />}
            <Button type="submit" className="w-full" disabled={sending}>
              {sending ? "Sending…" : "Email me a sign-in link"}
            </Button>
          </form>
        )}
        {config?.googleEnabled && !sentTo && (
          <>
            <div className="flex items-center gap-3 text-xs text-faint">
              <span className="h-px flex-1 bg-line" />or<span className="h-px flex-1 bg-line" />
            </div>
            <a
              href={`/api/auth/google/start?next=${encodeURIComponent(next)}&tz=${encodeURIComponent(tz)}`}
              className={cx(buttonClass("secondary"), "w-full")}
            >
              Continue with Google
            </a>
          </>
        )}
      </Card>
    </div>
  );
}

export function Verify() {
  const navigate = useNavigate();
  const { refreshMe } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The token arrives in the URL fragment. Read it once, then remove it from the address bar.
  const [link] = useState(() => {
    const p = new URLSearchParams(window.location.hash.slice(1));
    // `into`: this link adds the email to that account (and merges its own account) instead of signing in.
    return { token: p.get("token"), next: p.get("next") ?? "/", into: p.get("into") };
  });
  useEffect(() => {
    if (window.location.hash) history.replaceState(null, "", window.location.pathname);
  }, []);

  async function confirm() {
    if (!link.token || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ linked?: { movedContracts: number } }>("/api/auth/magic-link/verify", {
        method: "POST",
        json: { token: link.token, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      });
      await refreshMe();
      if (r.linked) return navigate(`/settings?linked=email&moved=${r.linked.movedContracts}`, { replace: true });
      navigate(link.next.startsWith("/") && !link.next.startsWith("//") ? link.next : "/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "link_used") {
        // Clicked twice, or already signed in on this device.
        try {
          await api("/api/me");
          await refreshMe();
          return navigate("/", { replace: true });
        } catch {
          // Not signed in; show the message.
        }
      }
      setError(err instanceof Error ? err.message : "Sign-in failed.");
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[radial-gradient(ellipse_at_top,var(--ck-accent-soft),transparent_60%)] px-4">
      <Card className="w-full max-w-sm space-y-4 text-center shadow-lg sm:p-8">
        <img src="/icons/icon.svg" alt="" className="mx-auto h-12 w-12" />
        <h1 className="text-xl font-semibold tracking-tight">{link.into ? "Add this email to ClauseKeep" : "Sign in to ClauseKeep"}</h1>
        {!link.token ? (
          <Alert tone="bad">This sign-in link is incomplete. Open the link from the email again, or request a new one.</Alert>
        ) : error ? (
          <Alert tone="bad">{error}</Alert>
        ) : link.into ? (
          <div className="space-y-2 text-left text-sm text-muted">
            <p>
              This email will sign in to <strong className="text-fg">{link.into}</strong>'s account.
            </p>
            <p>If it already has its own ClauseKeep account, its contracts and files move into that workspace and the old account closes. This can't be undone.</p>
          </div>
        ) : (
          <p className="text-sm text-muted">Click to finish signing in on this device.</p>
        )}
        {link.token && !error && (
          <Button className="w-full" onClick={confirm} disabled={busy}>
            {busy ? (link.into ? "Adding…" : "Signing in…") : link.into ? "Add email and merge" : "Continue to ClauseKeep"}
          </Button>
        )}
        {(error || !link.token) && (
          <Button variant="secondary" className="w-full" onClick={() => navigate("/signin")}>
            Request a new link
          </Button>
        )}
      </Card>
    </div>
  );
}
